import { useMemo } from "react";
import {
  Alerta,
  AvisoDeExemplo,
  Caminho,
  Cartao,
  Indicador,
  SeletorDeEmpresa,
  TODAS,
} from "@plantus/ui";

import { usarAmbiente } from "../../../nucleo/ambiente/ProvedorDeAmbiente";
import { usarEmpresas } from "../../../nucleo/empresa/ProvedorDeEmpresas";
import { caminhoDaRota } from "../../../nucleo/navegacao/caminho";
import { ROTAS } from "../../../nucleo/navegacao/rotas";
import { BlocoDeHabilitacoes } from "./BlocoDeHabilitacoes";
import { baseDeCalculo, resumir } from "./cobertura";
import { dadosDoPainel } from "./dadosDeExemplo";

/**
 * O painel de risco — a tela que se abre depois do login.
 *
 * <p><b>Não é um CRUD com gráficos.</b> A doc do setor decidiu isto: a entrega
 * central de Assuntos Regulatórios é um painel de risco, e o cadastro existe
 * para alimentá-lo.
 *
 * <p>E a diretoria <b>abre esta tela sozinha</b>, sem ninguém explicando o que
 * cada número significa. Três consequências, todas visíveis no código abaixo:
 *
 * <ol>
 *   <li>Todo número traz prazo, empresa e consequência.</li>
 *   <li>O painel diz <b>o que fazer</b>, não só o que está errado.</li>
 *   <li>Ele declara <b>o que não sabe</b> — a cobertura vem antes dos números,
 *       não num rodapé.</li>
 * </ol>
 *
 * <h2>🔴 As empresas vêm da API, e só de lá (#24)</h2>
 *
 * <p>Esta tela tinha a <b>sua própria</b> lista das três pessoas jurídicas, com
 * nome e apelido escritos à mão, ao lado da que o {@code ProvedorDeEmpresas} já
 * lia de {@code GET /api/empresas}. Duas fontes da verdade para o mesmo fato
 * divergem sem barulho: a tela continua desenhando, com o dado velho — e o que
 * está do outro lado é o CNPJ que identifica o detentor de cada documento
 * regulado (ADR-0005 §1).
 *
 * <p>O painel fala <b>slug</b>: é ele que o dado de exemplo referencia, e é ele
 * que não muda quando a razão social muda (ADR-0005, R3 da 5.1.1). Quem traduz
 * slug em nome é a lista da API, nunca uma constante daqui.
 *
 * <h2>Carregando e falhando, sem tela em branco</h2>
 *
 * <p>Consultar a API introduziu dois estados que esta tela não tinha. Nenhum
 * deles pode apagá-la: o painel é a primeira coisa que se vê depois do login, e
 * os números não dependem das empresas — o filtro depende. Enquanto a lista não
 * chega, o lugar do seletor diz que está carregando; se ela não vier, um aviso
 * diz que o filtro está indisponível e que o que se lê abaixo é <b>o grupo
 * inteiro</b>. Em nenhum dos dois o painel some.
 */
export function PaginaPainel() {
  const { ambiente } = usarAmbiente();
  const { lista, carregando, erro, escolha, escolher } = usarEmpresas();

  const dados = dadosDoPainel(ambiente);

  /*
   * A empresa escolhida é a do sistema inteiro, não uma segunda escolha só
   * desta tela — quem alterna entre três o dia inteiro não pode ter duas telas
   * discordando sobre em qual delas está (ADR-0005 §10).
   *
   * `escolha ?? TODAS` é LEITURA, e a diferença importa: o padrão do painel é
   * o consolidado, porque a pergunta da diretoria é sobre o grupo — mas o
   * painel não ESCREVE essa escolha ao abrir. Se escrevesse, quem saísse daqui
   * para o recebimento chegaria com uma empresa já escolhida por inércia, que é
   * exatamente como um lote nasce no CNPJ errado.
   */
  const escolhida = useMemo(
    () => (escolha === null || escolha === TODAS
      ? null
      : (lista.find((e) => e.id === escolha) ?? null)),
    [escolha, lista],
  );
  const filtro = escolhida?.slug ?? null;

  // Slug → empresa. O nome de uma empresa é dado do servidor; aqui só se
  // procura. Sem a lista (carregando, ou falhou), sobra o slug — que é feio e
  // é identificação: campo em branco não diz se o dado não existe ou se alguém
  // esqueceu de preencher, e essa decisão já está tomada no `SeloDeEmpresa`.
  const porSlug = useMemo(() => new Map(lista.map((e) => [e.slug, e])), [lista]);
  const nomeDe = (slug: string) => porSlug.get(slug)?.nomeFantasia ?? slug;
  const apelidoDe = (slug: string) => porSlug.get(slug)?.apelido ?? slug;

  const habilitacoes = filtro === null
    ? dados.habilitacoes
    : dados.habilitacoes.filter((h) => h.quemDepende.some((q) => q === filtro));

  const resumo = resumir(dados.cobertura, filtro);
  const base = baseDeCalculo(resumo);
  const { indicadores } = dados;

  return (
    /* Painel é tela DENSA — ver "A LEI DE LARGURA" em `app.css`. Ele é leitura,
       mas leitura em BLOCOS: quem limita a linha de texto é o cartão, e o teto
       de 34rem na coluna deixaria 640px de papel ao lado de três blocos em
       coluna única. Cada bloco declara o próprio conforto. */
    <div className="painel" data-largura="densa">
      <header className="painel__topo">
        <div>
          {/* O chapéu dizia "Painel de risco" e PARAVA AÍ — sem o setor, que é
              a metade que separa esta tela de um painel de outro assunto. O
              caminho traz os dois porque os deriva do mesmo mapa que desenha a
              lateral, em vez de repetir uma string escrita à mão. */}
          {/* Sem `aoNavegar`: nesta tela nenhum degrau leva a lugar nenhum — o
              setor agrupa e não tem tela, e o painel é onde já se está. */}
          <Caminho passos={caminhoDaRota(ROTAS.painel)} />
          <h1 className="painel__titulo">Assuntos Regulatórios</h1>
          <p className="painel__subtitulo">
            {escolhida?.nomeFantasia ?? "Todas as empresas do grupo"}
          </p>
        </div>

        {/* Três estados no lugar de um, e nenhum deles é o vazio: um seletor
            que some sem explicação é indistinguível de um sistema com uma
            empresa só. */}
        {carregando ? (
          <p className="painel__empresas-estado" role="status">
            Carregando as empresas…
          </p>
        ) : erro ? (
          // Sem `role="status"`: quem anuncia a falha é o `Alerta` logo abaixo,
          // e duas regiões vivas dizendo a mesma coisa é ruído para quem ouve a
          // tela. Aqui o texto existe para o OLHO não achar que o seletor
          // simplesmente não existe nesta instalação.
          <p className="painel__empresas-estado">Filtro por empresa indisponível</p>
        ) : (
          <SeletorDeEmpresa
            empresas={lista.map((e) => ({
              id: e.id,
              // A razão social vai no `title` do chip; o apelido é o que cabe
              // nele. Os dois vêm do servidor — ver `SeletorDeEmpresa`.
              nome: e.razaoSocial,
              apelido: e.apelido,
              papel: e.papel,
            }))}
            selecionada={escolha ?? TODAS}
            aoSelecionar={escolher}
          />
        )}
      </header>

      {dados.saoExemplo && <AvisoDeExemplo />}

      {/* A falha não pode passar por "o grupo tem uma empresa só". Ela também
          não apaga o painel: os números desta tela não vêm da lista de
          empresas — o filtro vem. */}
      {erro && (
        <Alerta tom="atencao" titulo="Não foi possível carregar as empresas">
          O filtro por empresa está indisponível nesta sessão. Os números abaixo
          continuam valendo para <strong>o grupo inteiro</strong>, e as
          habilitações aparecem identificadas pelo código da empresa. Recarregue
          a página para tentar de novo.
        </Alerta>
      )}

      {/*
        A cobertura vem ANTES dos números, e não num rodapé. Ela é a
        qualificação de tudo o que se lê depois: enquanto ela não subir,
        nenhum indicador desta tela afirma "está tudo bem" — no máximo
        "do que eu conheço, está tudo bem".
      */}
      <Alerta
        tom={resumo.percentual !== null && resumo.percentual >= 90 ? "informacao" : "atencao"}
        titulo="Do que este sistema conhece"
      >
        {/*
          O ponto final é do JSX, e não do texto: `baseDeCalculo` devolve um
          FRAGMENTO de frase ("sobre 12 de ~300 produtos declarados") porque o
          mesmo valor vai como legenda de cada `Indicador`, onde ponto final
          não cabe. Sem ele, os dois trechos saíam emendados na tela —
          "…cobertura ~30% Enquanto a carga não terminar…" —, lidos como uma
          frase só. A pontuação é a única coisa que separa duas afirmações
          diferentes aqui: o que o sistema conhece, e o que isso implica para
          os números abaixo.
        */}
        {base}
        {resumo.percentual !== null && <> · cobertura ~{resumo.percentual}%</>}
        {"."}
        {(resumo.percentual === null || resumo.percentual < 90) && (
          <>
            {" "}
            Enquanto a carga não terminar, nenhum número abaixo afirma que está
            tudo bem — apenas que, <em>do que se conhece</em>, está.
          </>
        )}
      </Alerta>

      {/*
        Habilitações fica FORA de `.painel__blocos`, e isso é layout dito no
        lugar certo. Ele ocupava a linha inteira por um `:first-child` na
        folha de estilo — um seletor de POSIÇÃO decidindo hierarquia de
        conteúdo, que passa a mentir no dia em que alguém reordenar os
        cartões. Aqui ele é irmão do bloco de colunas: ocupa a largura
        inteira por ser filho direto de `.painel`, e ninguém precisa saber
        que ele é o primeiro de coisa nenhuma.

        E ele é mesmo o de cima: sem alvará a fábrica para, e o risco herdado
        de uma empresa para a outra só existe neste bloco.
      */}
      <Cartao titulo="Habilitações">
        <h2 className="painel__bloco-titulo">Habilitações</h2>
        <p className="painel__bloco-apoio">
          Sem alvará, a fábrica daquela empresa para. Sem contrato de locação,
          quem opera sob ele para junto.
        </p>
        <BlocoDeHabilitacoes itens={habilitacoes} nomeDe={nomeDe} apelidoDe={apelidoDe} />
      </Cartao>

      <div className="painel__blocos">
        <Cartao titulo="Fichas de segurança">
          <h2 className="painel__bloco-titulo">Fichas de segurança</h2>
          <p className="painel__bloco-apoio">
            Para insumo isento ou dispensado, a ficha <strong>é</strong> o
            artefato de conformidade — não há registro para exibir no lugar dela.
            {" "}
            {/*
              Os números aqui contam PRODUTOS, não fichas. A ficha tem vínculo
              obrigatório com um produto e não existe solta; cada número abre
              uma lista de produtos filtrada, não um cadastro de fichas.
            */}
            Cada número abre a lista de <strong>produtos</strong> naquela
            situação.
          </p>

          <Indicador
            rotulo="Produtos com ficha vencida"
            valor={indicadores.fichasVencidas}
            base={base}
            cobertura={resumo.percentual ?? undefined}
            tom={indicadores.fichasVencidas > 0 ? "perigo" : "neutro"}
          />
          <Indicador
            rotulo="Produtos com ficha a revisar"
            valor={indicadores.fichasARevisar}
            base={base}
            tom={indicadores.fichasARevisar > 0 ? "atencao" : "neutro"}
          />
          <Indicador
            rotulo="Isentos e dispensados sem ficha"
            valor={indicadores.isentosSemFicha}
            base={base}
            tom={indicadores.isentosSemFicha > 0 ? "perigo" : "neutro"}
          />
        </Cartao>

        <Cartao titulo="Regularizações">
          <h2 className="painel__bloco-titulo">Regularizações</h2>
          <p className="painel__bloco-apoio">
            A maior parte do catálogo é isenta ou dispensada — o que aparece aqui
            é o que de fato vence. Também abre a lista de produtos.
          </p>

          <Indicador
            rotulo="Vencendo em 6 meses"
            valor={indicadores.regularizacoesVencendo}
            base={base}
            cobertura={resumo.percentual ?? undefined}
            tom={indicadores.regularizacoesVencendo > 0 ? "atencao" : "neutro"}
          />
        </Cartao>

        <Cartao titulo="Petições">
          <h2 className="painel__bloco-titulo">Petições</h2>
          <p className="painel__bloco-apoio">
            Prazo de exigência não espera — e petição parada costuma estar
            esperando alguém daqui.
          </p>

          <Indicador
            rotulo="Exigências com prazo correndo"
            valor={indicadores.exigenciasComPrazo}
            base="sobre as petições cadastradas"
            tom={indicadores.exigenciasComPrazo > 0 ? "perigo" : "neutro"}
          />
          <Indicador
            rotulo="Petições paradas"
            valor={indicadores.peticoesParadas}
            base="sem movimentação além do prazo esperado"
            tom={indicadores.peticoesParadas > 0 ? "atencao" : "neutro"}
          />
        </Cartao>
      </div>

      {/* ⚠️ A frase mudou de tamanho quando o seletor passou a consultar a API:
          "nenhum bloco consulta a API" deixou de ser verdade, e uma tela
          regulada que afirma menos do que faz é tão desatualizada quanto a que
          afirma mais. O que continua valendo — e é o que importa — é que
          nenhum NÚMERO daqui vem do sistema. */}
      <p className="painel__rodape">
        As empresas vêm da API; <strong>nenhum número desta tela vem</strong> —
        os módulos de negócio vêm a partir do Marco 3. Os números de ficha e
        regularização vão abrir listas de <strong>produtos</strong> filtradas:
        elas não existem soltas.
      </p>
    </div>
  );
}
